import React, { useState, useEffect } from 'react';
import { api } from '../App';
import '../styles/BestellerDashboardPage.css';
import '../styles/PageHero.css';
import { getSpeltakImage } from '../utils/speltakImages';
import NieuweBestellingPage from './NieuweBestellingPage';
import BestellingenOverzichtPage from './BestellingenOverzichtPage';
import BestellingDetailsPage from './BestellingDetailsPage';
import BestellingBewerkenPage from './BestellingBewerkenPage';

function formatTodayDmy() {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(today.getDate())}-${pad(today.getMonth() + 1)}-${today.getFullYear()}`;
}

function parseBestellerHash() {
  const hash = window.location.hash.slice(1);
  if (!hash) return { page: 'menu', orderId: null };
  if (hash === 'neworder') return { page: 'neworder', orderId: null };
  if (hash === 'bestellingen') return { page: 'bestellingen', orderId: null };
  if (hash.startsWith('details-')) {
    const orderId = parseInt(hash.replace('details-', ''), 10);
    return { page: 'details', orderId: orderId || null };
  }
  if (hash.startsWith('bewerken-')) {
    const orderId = parseInt(hash.replace('bewerken-', ''), 10);
    return { page: 'bewerken', orderId: orderId || null };
  }
  return { page: 'menu', orderId: null };
}

function BestellerDashboardPage({ user, onLogout }) {
  const initial = parseBestellerHash();
  const [currentPage, setCurrentPage] = useState(initial.page);
  const [selectedOrderId, setSelectedOrderId] = useState(initial.orderId);
  const [geenBestellingVandaag, setGeenBestellingVandaag] = useState(false);

  useEffect(() => {
    const onHashChange = () => {
      const parsed = parseBestellerHash();
      setCurrentPage(parsed.page);
      setSelectedOrderId(parsed.orderId);
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (currentPage === 'menu') {
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      return;
    }

    let hash = currentPage;
    if ((currentPage === 'details' || currentPage === 'bewerken') && selectedOrderId) {
      hash = `${currentPage}-${selectedOrderId}`;
    }

    const newHash = `#${hash}`;
    if (window.location.hash !== newHash) {
      window.location.hash = hash;
    }
  }, [currentPage, selectedOrderId]);

  useEffect(() => {
    if (currentPage !== 'menu') return;

    const checkBestellingVandaag = async () => {
      try {
        const response = await api.get('/index.php?action=mybestellingen');
        const vandaag = formatTodayDmy();
        const heeftVandaag = response.data.some((b) => b.besteldatum === vandaag);
        setGeenBestellingVandaag(!heeftVandaag);
      } catch {
        setGeenBestellingVandaag(false);
      }
    };

    checkBestellingVandaag();
  }, [currentPage]);

  const handleLogout = async () => {
    try {
      await api.post('/index.php?action=logout');
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (currentPage === 'neworder') {
    return <NieuweBestellingPage user={user} onBack={() => setCurrentPage('menu')} onLogout={handleLogout} />;
  }

  if (currentPage === 'bestellingen') {
    return (
      <BestellingenOverzichtPage
        user={user}
        onBack={() => setCurrentPage('menu')}
        onLogout={handleLogout}
        onNewOrder={() => setCurrentPage('neworder')}
        onSelectOrder={(orderId) => {
          setSelectedOrderId(orderId);
          setCurrentPage('details');
        }}
      />
    );
  }

  if (currentPage === 'details' && selectedOrderId) {
    return (
      <BestellingDetailsPage
        user={user}
        bestellingId={selectedOrderId}
        onBack={() => setCurrentPage('bestellingen')}
        onLogout={handleLogout}
        onEditOrder={(orderId) => {
          setSelectedOrderId(orderId);
          setCurrentPage('bewerken');
        }}
      />
    );
  }

  if (currentPage === 'bewerken' && selectedOrderId) {
    return (
      <BestellingBewerkenPage
        user={user}
        bestellingId={selectedOrderId}
        onBack={() => setCurrentPage('details')}
        onLogout={handleLogout}
      />
    );
  }

  const speltakNaam = user?.speltak_naam || user?.inlognaam;
  const speltakLogo = getSpeltakImage(speltakNaam);

  return (
    <div className="besteller-container">
      <div
        className="page-hero page-hero--besteller"
        style={{ backgroundImage: "url('/images/login-hero.jpg')" }}
      >
        <div className="hero-overlay" />
        {speltakLogo && (
          <div className="hero-speltak-logo-wrap">
            <img
              src={speltakLogo}
              alt={speltakNaam}
              className="hero-speltak-logo"
            />
          </div>
        )}
        <div className="hero-content dashboard-hero-text">
          <h2>Beheer je bestellingen</h2>
          <p>Kies wat je wilt doen</p>
        </div>
      </div>

      <header className="portal-subheader">
        <div className="header-content">
          <img src="/images/jmc-logo.png" alt="JMC Logo" className="logo-header" />
          <div className="header-title">
            <h1>Kampboodschappen</h1>
            <p>Bestellingsportal</p>
          </div>
        </div>
        <div className="user-info">
          <span className="user-name">{user?.inlognaam}</span>
          <span className="user-role">({user?.rol})</span>
          <button type="button" onClick={handleLogout} className="logout-button">
            Uitloggen
          </button>
        </div>
      </header>

      <main className="besteller-main">
        {geenBestellingVandaag && (
          <div
            className="besteller-reminder"
            onClick={() => setCurrentPage('neworder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setCurrentPage('neworder');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span className="besteller-reminder-icon">⚠️</span>
            <p>Je hebt voor vandaag nog geen boodschappen besteld, doe dit nu.</p>
          </div>
        )}

        <div className="besteller-menu">
          <div
            className="menu-card"
            onClick={() => setCurrentPage('neworder')}
          >
            <div className="menu-icon">➕</div>
            <h3>Nieuwe Bestelling</h3>
            <p>Plaats een nieuwe bestelling voor je speltak</p>
          </div>

          <div
            className="menu-card"
            onClick={() => setCurrentPage('bestellingen')}
          >
            <div className="menu-icon">📋</div>
            <h3>Mijn Bestellingen</h3>
            <p>Bekijk en beheer je bestaande bestellingen</p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default BestellerDashboardPage;
